import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { TranslateService } from "@ngx-translate/core";
import { jwtDecode } from "jwt-decode";
import { MessageService } from "primeng/api";
import { lastValueFrom, Observable, Subject, Subscription } from "rxjs";
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { ApsPermission } from "src/app/models/aps-permission/aps-permission";
import { ConstantsFordProtect } from "src/app/models/constants/constants-ford-protect";
import { UserToken } from "src/app/models/user/user-token";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: 'root'
})
export class ApsPermissionService {

  private apiUrl = environment.apiUrl;
  private apsPermission: ApsPermission;
  private userToken: UserToken;
  private snapshotUrl: string;
  private environment = environment;
  private readonly _destroying$ = new Subject<void>();
  private permissionBootstrapPromise: Promise<void> | null = null;
  private msalIdlePromise: Promise<void> | null = null;
  private hasBootstrappedPermissions = false;
  private readonly authMetricPrefix = 'apsAuthMetric';
  private readonly permissionsCacheKey = 'apsPermissionCache';

  constructor(private httpClient: HttpClient,
    private messageService: MessageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    console.log("Constructor ApsPermissionService");
    this.apsPermission = new ApsPermission();
    this.userToken = new UserToken();
    this.snapshotUrl = this.router.url;
    this.restoreCachedPermissions();
    this.listenToMsal();
  }

  private restoreCachedPermissions(): void {
    try {
      const cached = sessionStorage.getItem(this.permissionsCacheKey);
      if (!cached) {
        return;
      }

      const parsed = JSON.parse(cached) as ApsPermission;
      if (parsed?.Permitted && Array.isArray(parsed.Permitted)) {
        this.apsPermission = parsed;
        this.trackAuthMetric('cache.restore');
      }
    } catch {
      // ignore corrupted cache
    }
  }

  private listenToMsal() {

    console.log("Inicializando escuta MSAL...");
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        console.log("MSAL carregado, obtendo dados do usuário...");
        this.getUserData();
      });
  }

  private resolveActiveAccount(): AccountInfo | null {
    let account = this.authService.instance.getActiveAccount();

    if (!account) {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        this.authService.instance.setActiveAccount(account);
      }
    }

    return account;
  }

  private setUserTokenFromAccessToken(accessToken: string): void {
    const decodedToken = jwtDecode<{
      country?: string;
      cdsid?: string;
      preferred_username?: string;
    }>(accessToken);

    this.userToken.country = decodedToken['country'] ?? '';
    this.userToken.cdsid = decodedToken['cdsid'] ?? '';
    this.userToken.emailAddress = decodedToken['preferred_username'] ?? '';
    sessionStorage.setItem('userId', this.getUserCsid());
  }

  async ensureUserContextReady(): Promise<boolean> {
    await this.waitForMsalIdle();

    const scopes = this.environment.myApiConfig.scopes;
    const maxAttempts = 6;
    const retryDelayMs = 200;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const account = this.resolveActiveAccount();
      if (!account) {
        if (attempt < maxAttempts) {
          await this.delay(retryDelayMs);
          continue;
        }
        this.trackAuthMetric('context.accountMissing');
        return false;
      }

      try {
        const result = await this.authService.instance.acquireTokenSilent({
          scopes,
          account
        });

        this.setUserTokenFromAccessToken(result.accessToken);
        return true;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          this.trackAuthMetric('context.interactionRequired');
          this.authService.instance.acquireTokenRedirect({ scopes });
          return false;
        }

        if (attempt < maxAttempts) {
          await this.delay(retryDelayMs);
          continue;
        }

        this.trackAuthMetric('context.tokenSilentFailed');
        return false;
      }
    }

    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private waitForMsalIdle(): Promise<void> {
    if (this.msalIdlePromise) {
      return this.msalIdlePromise;
    }

    this.msalIdlePromise = new Promise<void>((resolve) => {
      const subscription: Subscription = this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          take(1),
          takeUntil(this._destroying$)
        )
        .subscribe(() => {
          resolve();
        });

      setTimeout(() => {
        subscription?.unsubscribe();
        resolve();
      }, 1500);
    }).finally(() => {
      this.msalIdlePromise = null;
    });

    return this.msalIdlePromise;
  }

  async bootstrapPermissions(): Promise<void> {
    if (this.permissionBootstrapPromise) {
      this.trackAuthMetric('bootstrap.reuse');
      return this.permissionBootstrapPromise;
    }

    if (this.hasBootstrappedPermissions) {
      this.trackAuthMetric('bootstrap.skip.ready');
      return;
    }

    this.trackAuthMetric('bootstrap.start');
    this.permissionBootstrapPromise = (async () => {
      const isContextReady = await this.ensureUserContextReady();
      if (!isContextReady) {
        this.trackAuthMetric('bootstrap.contextNotReady');
        return;
      }

      try {
        const response = await this.getPermissionsToPromise({ silent: true });
        if (response?.Permitted?.length > 0) {
          this.setApsPermission(response);
        }
        this.hasBootstrappedPermissions = true;
        this.trackAuthMetric('bootstrap.success');
      } catch (error) {
        this.trackAuthMetric('bootstrap.failed');
        console.warn('APS bootstrap permissions failed', error);
      }
    })()
      .finally(() => {
        this.permissionBootstrapPromise = null;
      });

    return this.permissionBootstrapPromise;
  }

  private getUserData() {

    console.log("Obtendo dados do usuário...");
    const account = this.resolveActiveAccount();

    if (!account) {
      console.warn("Nenhuma conta ativa ou em cache encontrada.");
      return;
    }

    const scopes = this.environment.myApiConfig.scopes;
    this.authService.instance.acquireTokenSilent({
      scopes: scopes,
      account: account
    })
      .then((res: AuthenticationResult) => {
        this.setUserTokenFromAccessToken(res.accessToken);
        console.log("País recuperado com sucesso: " + this.userToken.country?.substring(0, 2) + "*");
        console.log("Cdsid recuperado com sucesso: " + this.userToken.cdsid?.substring(0, 5) + "*****");
        console.log("Email recuperado com sucesso: " + this.userToken.emailAddress?.substring(0, 5) + "*****");
      })
      .catch((err) => {
        if (err instanceof InteractionRequiredAuthError) {
          this.authService.instance.acquireTokenRedirect({ scopes: scopes });
        } else {
          console.error("Erro ao obter token:", err);
        }
      });
  }

  setApsPermission(apsPermission: ApsPermission) {
    this.apsPermission = apsPermission;
    try {
      sessionStorage.setItem(this.permissionsCacheKey, JSON.stringify(apsPermission));
      this.trackAuthMetric('cache.store');
    } catch {
      // ignore storage errors
    }
  }

  getApsPermission(): ApsPermission {
    return this.apsPermission;
  }

  hasResolvedPermissions(): boolean {
    return this.hasBootstrappedPermissions;
  }

  async getPermission(): Promise<Observable<ApsPermission>> {
    return this.httpClient.get<ApsPermission>(`${this.apiUrl}/api/permission`)
      .pipe(
        map((response: ApsPermission) => {
          return response;
        })
      );
  }

  async getPermissionsToPromise(options?: { silent?: boolean }): Promise<ApsPermission> {
    try {
      const isContextReady = await this.ensureUserContextReady();
      if (!isContextReady) {
        this.trackAuthMetric('permission.contextNotReady');
        throw Object.assign(new Error('MSAL context not ready for permission request'), { status: 401 });
      }
      return await lastValueFrom(this.httpClient.get<ApsPermission>(`${this.apiUrl}/api/permission`));
    } catch (error: unknown) {
      const backendDetails = this.getBackendDetails(error);
      this.trackAuthMetric(`permission.error.${(error as { status?: number })?.status ?? 'unknown'}`);
      const err = this.toError('ApsPermissionService.getPermissionsToPromise failed', error);
      console.error(err.message, error, backendDetails);
      if (!options?.silent) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
        });
      }
      throw err;
    }
  }

  private trackAuthMetric(metric: string): void {
    const key = `${this.authMetricPrefix}.${metric}`;

    try {
      const current = Number(sessionStorage.getItem(key) || '0');
      const next = current + 1;
      sessionStorage.setItem(key, String(next));
      console.info(`[APS-METRIC] ${metric}=${next}`);
    } catch {
      console.info(`[APS-METRIC] ${metric}`);
    }
  }

  async getPermissionByPermissionName(name: string): Promise<boolean> {
    let isValid = false;
    await this.getPermissionsToPromise().then(lista => {
      lista.Permitted.forEach(permitted => {
        permitted.execute.forEach(permissionName => {
          if (permissionName === name) {
            isValid = true;
          }
        })
      })
    });
    return isValid;
  }

  async getListPermissions(): Promise<string[]> {

    const permissions: string[] = [];
    this.snapshotUrl = this.router.url;

    if (this.apsPermission?.Permitted?.length) {
      this.apsPermission.Permitted.forEach(permitted => {
        permitted.execute.forEach(permissionName => {
          permissions.push(permissionName);
        });
      });
      return permissions;
    }

    if (!this.snapshotUrl.includes(ConstantsFordProtect.OPEN_URL_FRONT)) {
      await this.getPermissionsToPromise().then(lista => {
        lista.Permitted.forEach(permitted => {
          permitted.execute.forEach(permissionName => {
            permissions.push(permissionName);
          })
        })
      });
    }
    return permissions;

  }

  async getSearchApsPermission() {

    try {
      const response = await this.getPermissionsToPromise();

      if (response.Permitted.length > 0) {
        this.setApsPermission(response);
      } else {
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Usuário sem permissão.' });
      }
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      const backendDetails = this.getBackendDetails(err);

      if (status === 403 && this.isApsScopeIssue(err)) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: 'Falha na autorização APS: configuração de escopo inválida no backend (invalid_scope).'
        });
        console.error('APS invalid_scope detected in backend', backendDetails);
        return;
      }

      if (status === 400 || status === 404 || status === 500) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.USERS.NOT.EXIST')
        });
      } else if (status === 401) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
        });
      }
    }
  }

  permissionValidation(screenName: string): boolean {
    let isValid = false;
    if (this.apsPermission && this.apsPermission.Permitted) {
      this.apsPermission.Permitted.forEach(permitted => {
        if (permitted.execute && Array.isArray(permitted.execute)) {
          permitted.execute.forEach(permissionName => {
            if (screenName === permissionName) {
              isValid = true;
            }
          });
        }
      });
    }

    return isValid;
  }

  isPermissionValidationDisabled(screenName: string): boolean {
    let isValid = true;
    this.apsPermission?.Permitted.forEach(permitted => {
      permitted.execute.forEach(permissionName => {
        if (screenName === permissionName) {
          isValid = false;
        }
      });
    });

    return isValid;
  }

  getUserCsid(): string {
    return this.userToken.cdsid;
  }

  getUserCountry(): string {
    return this.userToken.country;
  }

  getUserEmail(): string {
    return this.userToken.emailAddress;
  }

  private toError(context: string, error: unknown): Error {
    const status = (error as { status?: number })?.status;
    const message = error instanceof Error ? error.message : String(error);
    const normalized = new Error(`${context}: ${message}`);

    if (typeof status === 'number') {
      (normalized as Error & { status?: number }).status = status;
    }

    return normalized;
  }

  private getBackendDetails(error: unknown): string {
    const payload = (error as { error?: { error_description?: string; exceptionMessage?: string; message?: string } })?.error;
    return `${payload?.error_description ?? ''} ${payload?.exceptionMessage ?? ''} ${payload?.message ?? ''}`.trim().toLowerCase();
  }

  private isApsScopeIssue(error: unknown): boolean {
    const details = this.getBackendDetails(error);
    return details.includes('invalid_scope')
      || details.includes('scope null')
      || details.includes('invoking aps service');
  }

}